import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../lib/firebase";

/**
 * /not-authorized — friendly dead-end for signed-in users whose email isn't
 * on the `identity.cms_users` whitelist. The NestJS guard returns 403 with
 * `error: "not_whitelisted"`; api.ts converts that into a hard navigation
 * here (rather than rendering an error toast in-place — once the api can't
 * answer, the page chrome is misleading).
 *
 * The route lives OUTSIDE `<RequireAuth>` so the user lands here even
 * though their Firebase session is technically valid. They can sign out
 * and try a different account, or contact us.
 */
export function NotAuthorized() {
  const navigate = useNavigate();
  const [email, setEmail] = useState<string | null>(null);
  const [signingOut, setSigningOut] = useState(false);

  // Display the currently-signed-in email so it's obvious which account
  // got rejected — non-obvious otherwise if the user keeps multiple
  // Google accounts open.
  useEffect(() => {
    const unsub = auth.onAuthStateChanged((u) => setEmail(u?.email ?? null));
    return unsub;
  }, []);

  async function signOutAndRetry() {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await auth.signOut();
      navigate("/login", { replace: true });
    } catch {
      // Even if sign-out fails (rare), get them back to /login so they
      // can re-attempt with a different account picker.
      navigate("/login", { replace: true });
    }
  }

  return (
    <div className="min-h-svh flex items-center justify-center bg-neutral-50 px-6">
      <div className="max-w-md w-full bg-white rounded-lg border border-neutral-200 shadow-sm p-8 text-center">
        <div className="mx-auto w-12 h-12 rounded-full bg-rose-50 border border-rose-200 flex items-center justify-center mb-4">
          <svg
            width="20"
            height="20"
            viewBox="0 0 16 16"
            aria-hidden
            className="text-rose-600"
          >
            <path
              d="M5 6V4.5a3 3 0 0 1 6 0V6M3 6h10v8H3z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle cx="8" cy="10" r="0.9" fill="currentColor" />
          </svg>
        </div>

        <h1 className="text-[20px] font-semibold text-neutral-900 mb-2">
          Access denied
        </h1>

        {email ? (
          <p className="text-[13px] text-neutral-600 leading-relaxed mb-1">
            <span className="font-mono text-neutral-900">{email}</span> isn't
            on the Leaply admin whitelist.
          </p>
        ) : (
          <p className="text-[13px] text-neutral-600 leading-relaxed mb-1">
            This account isn't on the Leaply admin whitelist.
          </p>
        )}

        <p className="text-[12px] text-neutral-500 leading-relaxed mb-6">
          Admin access is granted by adding your email to the{" "}
          <code className="font-mono">identity.cms_users</code> table with{" "}
          <code className="font-mono">active = true</code> and{" "}
          <code className="font-mono">role = 'admin'</code>. Ask whoever set
          up Leaply CMS access — same allowlist as cms-admin.
        </p>

        <button
          type="button"
          onClick={() => void signOutAndRetry()}
          disabled={signingOut}
          className="w-full rounded-md bg-neutral-900 hover:bg-neutral-800 disabled:bg-neutral-300 px-4 py-2.5 text-[13px] font-medium text-white transition-colors"
        >
          {signingOut ? "Signing out…" : "Sign out and try a different account"}
        </button>

        <p className="mt-4 text-[11px] text-neutral-400">
          If you think this is a mistake, contact{" "}
          <a
            href="mailto:tetiana.fomina@skelar.tech"
            className="text-neutral-600 hover:text-neutral-900 hover:underline"
          >
            tetiana.fomina@skelar.tech
          </a>
          .
        </p>
      </div>
    </div>
  );
}
